// Settings › Order recovery — every field of settings/reassignment (see ORDER_RECOVERY_PLAN.md §3.3).
// Writes with set({merge:true}); functions cache the doc for 5 minutes.
import Switch from 'rc-switch';
import 'rc-switch/assets/index.css';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, CustomInput, Input, InputGroup, InputGroupAddon, InputGroupText } from 'reactstrap';
import { firestore } from '../../../helpers/Firebase';
import { createNotification } from '../../../helpers/Utils';
import { Field, SettingList, SettingRow, SettingsCard, StatusPill } from './SettingsLayout';

const DEFAULTS = {
  active: false,
  windowMinutes: 10, maxCandidates: 3, maxAttempts: 2, replacementAcceptMinutes: 10,
  enabledServiceTypes: ['washingService', 'onSiteWash'], minLeadMinutes: 45, maxDistanceKm: 25,
  slotToleranceMinutes: 60, maxPriceIncreasePct: 30,
  weights: { rating: 0.35, distance: 0.25, time: 0.25, price: 0.15 },
  minServiceRatingCount: 3, excludeSpCancelRatioAbove: 0.25, excludeIfRejectedSameCustomerDays: 30, quickWashRebroadcast: true,
  whatsappOfferTemplate: '',
};
const SERVICE_TYPES = [
  { key: 'washingService', label: 'Car wash (scheduled)' },
  { key: 'onSiteWash', label: 'On-site wash' },
];
const numOrNull = (v) => (v === '' || v == null ? null : Number(v));

const OrderRecoverySettings = () => {
  const [s, setS] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    firestore().collection('settings').doc('reassignment').get().then((snap) => {
      const d = snap.exists ? snap.data() : {};
      const merged = { ...DEFAULTS, ...d, weights: { ...DEFAULTS.weights, ...(d.weights || {}) } };
      setS(merged);
      setUpdatedAt(d.updatedAt || null);
    }).catch((e) => createNotification('error', (e && e.message) || 'Could not load settings', 'Order recovery'))
      .finally(() => setLoading(false));
  }, []);

  const set = useCallback((k, v) => { setS((prev) => ({ ...prev, [k]: v })); setDirty(true); }, []);
  const setW = useCallback((k, v) => { setS((prev) => ({ ...prev, weights: { ...prev.weights, [k]: v } })); setDirty(true); }, []);
  const weightSum = Object.values(s.weights || {}).reduce((a, b) => a + Number(b || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        active: !!s.active,
        // test-rollout lists retired (2026-10-15) — drop the old fields
        testCustomerIds: firestore.FieldValue.delete(),
        candidateSpIdsAllowlist: firestore.FieldValue.delete(),
        windowMinutes: Number(s.windowMinutes) || 0,
        maxCandidates: Number(s.maxCandidates) || 3,
        maxAttempts: Number(s.maxAttempts) || 2,
        replacementAcceptMinutes: Math.max(1, Number(s.replacementAcceptMinutes) || 10),
        enabledServiceTypes: Array.isArray(s.enabledServiceTypes) ? s.enabledServiceTypes : DEFAULTS.enabledServiceTypes,
        minLeadMinutes: Number(s.minLeadMinutes) || 0,
        maxDistanceKm: Number(s.maxDistanceKm) || 25,
        slotToleranceMinutes: Number(s.slotToleranceMinutes) || 0,
        maxPriceIncreasePct: Number(s.maxPriceIncreasePct) || 0,
        weights: { rating: Number(s.weights.rating) || 0, distance: Number(s.weights.distance) || 0, time: Number(s.weights.time) || 0, price: Number(s.weights.price) || 0 },
        minServiceRatingCount: Number(s.minServiceRatingCount) || 3,
        excludeSpCancelRatioAbove: numOrNull(s.excludeSpCancelRatioAbove),
        excludeIfRejectedSameCustomerDays: Number(s.excludeIfRejectedSameCustomerDays) || 0,
        quickWashRebroadcast: !!s.quickWashRebroadcast,
        whatsappOfferTemplate: String(s.whatsappOfferTemplate || '').trim(),
        updatedAt: Date.now(),
      };
      await firestore().collection('settings').doc('reassignment').set(payload, { merge: true });
      setUpdatedAt(payload.updatedAt);
      setDirty(false);
      createNotification('success', 'Order recovery settings saved. Running functions pick them up within 5 minutes.', 'Settings');
    } catch (e) {
      createNotification('error', (e && e.message) || 'Save failed', 'Settings');
    } finally { setSaving(false); }
  };

  // One numeric setting row: label + help left, input with its unit right.
  const num = (label, key, help, unit, step = 1) => (
    <SettingRow label={label} help={help}>
      <InputGroup>
        <Input type="number" step={step} value={s[key] == null ? '' : s[key]} onChange={(e) => set(key, e.target.value)} />
        {unit ? <InputGroupAddon addonType="append"><InputGroupText>{unit}</InputGroupText></InputGroupAddon> : null}
      </InputGroup>
    </SettingRow>
  );

  if (loading) return <div className="loading" />;

  const footer = (
    <>
      <span className="st-foot-note">{updatedAt ? `Last saved ${new Date(Number(updatedAt)).toLocaleString()}` : 'Never saved'}{dirty ? ' · unsaved changes' : ''}</span>
      <Button color="primary" disabled={saving || !dirty} onClick={save}>{saving ? 'Saving…' : 'Save changes'}</Button>
    </>
  );

  return (
    <SettingsCard
      title="Order recovery"
      description="When a provider rejects or cancels, find a replacement automatically instead of cancelling the customer."
      status={<StatusPill on={!!s.active} offLabel="Off" onLabel="Live for everyone" />}
      footer={footer}
    >
      <div className="st-section">
        <div className="st-section-title">Rollout</div>
        <div className="st-switch-row">
          <div className="st-switch-text">
            <b>Order recovery {s.active ? 'is on' : 'is off'}</b>
            <span>Master switch. While on, recovery runs for every customer of the covered services. While off, provider rejections behave exactly as before and no customer is contacted.</span>
          </div>
          <Switch className="custom-switch custom-switch-primary" checked={!!s.active} onChange={(v) => set('active', v)} />
        </div>
      </div>

      <div className="st-section">
        <div className="st-section-title">Which bookings are rescued</div>
        <SettingList>
          <SettingRow label="Services covered" help="Only these service types are rescued when their provider rejects or cancels.">
            {SERVICE_TYPES.map((t) => (
              <CustomInput key={t.key} type="checkbox" id={`st-svc-${t.key}`} label={t.label}
                checked={(s.enabledServiceTypes || []).includes(t.key)}
                onChange={(e) => set('enabledServiceTypes', e.target.checked ? [...new Set([...(s.enabledServiceTypes || []), t.key])] : (s.enabledServiceTypes || []).filter((k) => k !== t.key))} />
            ))}
          </SettingRow>
          <SettingRow label="Quick wash fan-out" help="Widen the quick-wash fan-out once to more providers before the order auto-cancels.">
            <Switch className="custom-switch custom-switch-primary" checked={!!s.quickWashRebroadcast} onChange={(v) => set('quickWashRebroadcast', v)} />
          </SettingRow>
          {num('Minimum lead time', 'minLeadMinutes', 'Bookings closer than this to their slot are not rescued.', 'min')}
          {num('Max attempts', 'maxAttempts', 'How many times a booking may be rescued if its replacement is also rejected or not accepted in time.', 'times')}
        </SettingList>
      </div>

      <div className="st-section">
        <div className="st-section-title">Choosing a replacement</div>
        <SettingList>
          {num('Slot tolerance', 'slotToleranceMinutes', 'How far the replacement slot may move from the original, same day. Lead time decides whether to rescue; tolerance decides how far the time can shift.', 'min')}
          {num('Max distance', 'maxDistanceKm', 'Farthest a replacement provider can be.', 'km')}
          {num('Max price increase', 'maxPriceIncreasePct', 'Chill absorbs a dearer replacement up to this; dearer candidates are excluded.', '%')}
          {num('Candidates offered', 'maxCandidates', 'How many replacement providers the customer is shown.', 'providers')}
        </SettingList>
        <div className="st-subtitle">Skip providers that…</div>
        <SettingList>
          {num('Cancel too often', 'excludeSpCancelRatioAbove', 'Cancelled ÷ total bookings above this are skipped. Leave empty to turn the filter off.', 'ratio', 0.05)}
          {num('Rejected this customer recently', 'excludeIfRejectedSameCustomerDays', 'Providers that rejected the same customer within this many days are skipped. 0 turns it off.', 'days')}
        </SettingList>
      </div>

      <div className="st-section">
        <div className="st-section-title">Response times and messages</div>
        <SettingList>
          {num('Customer selection window', 'windowMinutes', 'How long the customer has to pick a replacement; when it ends the best match is assigned. 0 assigns the best match straight away.', 'min')}
          {num('Provider acceptance window', 'replacementAcceptMinutes', 'The replacement provider must accept within this. If they reject or do not answer, it moves to the next candidate (up to Max attempts), then the booking is cancelled and refunded.', 'min')}
          <SettingRow label="WhatsApp offer template" help="Meta-approved template for the customer's pick offer (see WHATSAPP_TEMPLATES.md). Empty = push and inbox only.">
            <Input type="text" value={s.whatsappOfferTemplate || ''} placeholder="order_replacement_offer" onChange={(e) => set('whatsappOfferTemplate', e.target.value)} />
          </SettingRow>
        </SettingList>
      </div>

      <div className="st-section">
        <div className="st-section-title">Ranking weights <span className={Math.abs(weightSum - 1) > 0.01 ? 'text-danger' : 'text-muted'} style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>· sum {weightSum.toFixed(2)}{Math.abs(weightSum - 1) > 0.01 ? ' (should be 1.00)' : ' ✓'}</span></div>
        <div className="st-weights">
          {[['rating', 'Rating'], ['distance', 'Distance'], ['time', 'Slot closeness'], ['price', 'Price']].map(([k, label]) => (
            <Field key={k} label={label}>
              <Input type="number" step="0.05" min="0" max="1" value={s.weights[k]} onChange={(e) => setW(k, e.target.value)} />
            </Field>
          ))}
        </div>
        <div style={{ marginTop: 12 }}>
          <SettingList>
            {num('Min ratings for per-service score', 'minServiceRatingCount', 'Below this many ratings for the service, the provider’s overall rating is used instead.', 'ratings')}
          </SettingList>
        </div>
      </div>
    </SettingsCard>
  );
};

export default OrderRecoverySettings;
